import React from 'react'
import PropTypes from 'prop-types'
import { Grid, Box, Typography, useTheme } from '@mui/material'
import { useSmartTranslation } from '../../../../../generic/hooks/useSmartTranslation'

/**
 * JobProgress Component
 * Displays the current progress of the job with a linear progress bar and details.
 *
 * @param {Object} props
 * @param {number} props.completed
 * @param {number} props.target
 * @param {number} props.nok
 */
const JobProgress = ({ completed = 0, target = 0, nok = 0 }) => {
  const theme = useTheme()
  const { t_ } = useSmartTranslation()

  const totalReported = completed + nok
  const progressPercentage = target > 0 ? Math.min((totalReported / target) * 100, 100) : 0
  const remaining = target - totalReported
  const okPct = target > 0 ? Math.min((completed / target) * 100, 100) : 0
  const nokPctRaw = target > 0 ? Math.min((nok / target) * 100, 100) : 0
  const nokPct = Math.min(nokPctRaw, Math.max(0, 100 - okPct))

  return (
    <Grid item xs={12} md={7} lg={8}>
      <Box sx={{ bgcolor: 'background.default', p: 2, borderRadius: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant='caption' sx={{ textTransform: 'uppercase', fontWeight: 'bold' }}>
            {t_('Progress')}
          </Typography>
          <Typography variant='h6' color='success.main' sx={{ lineHeight: 1 }}>
            {Math.round(progressPercentage)}%
          </Typography>
        </Box>
        <Box
          sx={{
            position: 'relative',
            height: 6,
            borderRadius: 3,
            mb: 1,
            overflow: 'hidden',
            bgcolor: theme.palette.action.disabledBackground
          }}
          role='progressbar'
          aria-valuenow={Math.round(progressPercentage)}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label={t_('Progress: OK {{ok}} NOK {{nok}} of {{target}}', { ok: completed, nok, target })}
        >
          <Box
            sx={{
              position: 'absolute',
              left: 0,
              top: 0,
              bottom: 0,
              width: okPct + '%',
              bgcolor: theme.palette.success.main
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              right: 0,
              top: 0,
              bottom: 0,
              width: nokPct + '%',
              bgcolor: theme.palette.error.main
            }}
          />
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant='body2' sx={{ fontWeight: 'bold' }}>
            <span style={{ color: theme.palette.success.main }}>{t_('OK')} {completed}</span>{' '}
            <span style={{ color: theme.palette.error.main }}>{t_('NOK')} {nok}</span>
          </Typography>
        </Box>
        <Typography variant='caption' color='text.secondary' display='block' sx={{ mt: 0.5 }}>
          {t_('Remaining')}: {remaining > 0 ? remaining : 0}
        </Typography>
      </Box>
    </Grid>
  )
}

JobProgress.propTypes = {
  completed: PropTypes.number,
  target: PropTypes.number,
  nok: PropTypes.number
}

export default JobProgress
